import { HttpErrorResponse, HttpStatusCode } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { forkJoin, of, timer } from "rxjs";
import {
  auditTime,
  catchError,
  filter,
  map,
  switchMap,
  withLatestFrom,
} from "rxjs/operators";
import { Ranking } from "src/app/shared/interfaces/ranking";
import { RankingScope } from "src/app/shared/interfaces/ranking-scope";
import { RankingCriteria } from "src/app/shared/models/ranking-criteria";
import { RankingService } from "src/app/shared/services/ranking/ranking.service";
import { Guid } from "src/app/shared/types/guid";
import * as fromRanking from "../actions/ranking.actions";
import * as fromRankingStore from "src/app/store/reducers/ranking.reducer";
import { findGroupScope } from "src/app/store/selectors/ranking.selectors";

const MANUAL_REFRESH_MIN_DURATION = 400;

@Injectable()
export class RankingEffect {
  private readonly criteria$ = this.store.select(
    (state) => state.ranking.rankingCriteria,
  );

  private readonly scopes$ = this.store.select((state) => state.ranking.scopes);

  private readonly getRankingCompleted$ = this.store.select(
    (state) => state.ranking.getRankingCompleted,
  );

  getRanking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromRanking.GET_RANKING, fromRanking.REFRESH_RANKING),
      switchMap(
        (action: fromRanking.GetRanking | fromRanking.RefreshRanking) => {
          const silent = action.type === fromRanking.REFRESH_RANKING;
          const ranking$ = this.rankingService.getRanking(
            action.payload,
            silent,
          );
          const manual =
            action.type === fromRanking.REFRESH_RANKING && action.manual;

          return (
            manual
              ? forkJoin([ranking$, timer(MANUAL_REFRESH_MIN_DURATION)]).pipe(
                  map(([ranking]) => ranking),
                )
              : ranking$
          ).pipe(
            map(
              (ranking: Ranking) =>
                new fromRanking.GetRankingCompleted(ranking),
            ),
            catchError((error: HttpErrorResponse) =>
              silent
                ? this.handleRefreshError(error)
                : of(new fromRanking.GetRankingFailed({ error: true })),
            ),
          );
        },
      ),
    ),
  );

  getRankingScopes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromRanking.GET_RANKING_SCOPES),
      switchMap(() =>
        this.rankingService.getScopes().pipe(
          map(
            (scopes: Array<RankingScope>) =>
              new fromRanking.GetRankingScopesCompleted(scopes),
          ),
          catchError(() => of(new fromRanking.GetRankingScopesFailed())),
        ),
      ),
    ),
  );

  rankingChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromRanking.RANKING_CHANGED),
      withLatestFrom(this.criteria$, this.scopes$),
      filter(
        ([action, criteria, scopes]: [
          fromRanking.RankingChanged,
          RankingCriteria,
          Array<RankingScope>,
        ]) =>
          this.router.url.includes("/ranking") &&
          this.isShown(action.payload.group_id, criteria, scopes),
      ),
      auditTime(2000),
      withLatestFrom(this.criteria$, this.getRankingCompleted$),
      filter(
        ([, , completed]: [unknown, RankingCriteria, boolean]) => completed,
      ),
      map(
        ([, criteria]: [unknown, RankingCriteria, boolean]) =>
          new fromRanking.RefreshRanking(criteria),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private rankingService: RankingService,
    private store: Store<{
      ranking: fromRankingStore.RankingState;
    }>,
    private router: Router,
  ) {}

  private handleRefreshError(error: HttpErrorResponse) {
    if (error.status === HttpStatusCode.NotFound) {
      return of(
        new fromRanking.GetRankingFailed({ error: false, unavailable: true }),
      );
    }

    if (error.status === HttpStatusCode.BadRequest) {
      this.router.navigate([], {
        queryParams: { page: null },
        queryParamsHandling: "merge",
        replaceUrl: true,
      });
    }

    return of(new fromRanking.RefreshRankingFailed());
  }

  private isShown(
    groupId: Guid,
    criteria: RankingCriteria,
    scopes: Array<RankingScope>,
  ): boolean {
    if (criteria.group) {
      return criteria.group === groupId;
    }

    const { organization, course } = findGroupScope(scopes, groupId);

    return (
      !!organization &&
      organization.id === criteria.organization &&
      course.id === criteria.course
    );
  }
}
